import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Layout } from '@/components/Layout';
import { useApi } from "@/hooks/useApi";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {  Users, Activity, Clock, Mail, MessageSquare, TrendingUp } from "lucide-react";
import { toast } from "sonner";

export const SystemAnalyticsPage = () => {
  const { user, token } = useAuth();
  const api = useApi(token);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);

  useEffect(() => {
    if (user?.role === "superadmin") {
      loadStats();
    }
  }, [user]);

  const loadStats = async () => {
    setLoading(true);
    try {
      const [activeUsers, emailStats, smsStats] = await Promise.all([
        api.get("/superadmin/active-users"),
        api.get("/superadmin/email-statistics"),
        api.get("/superadmin/sms-statistics")
      ]);
      setStats({
        activeUsers,
        emailStats,
        smsStats
      });
    } catch (error) {
      console.error("Failed to load system stats:", error);
      toast.error("Failed to load system analytics");
    } finally {
      setLoading(false);
    }
  };

  if (user?.role !== "superadmin") {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: "#fcfbf9" }}>
        <Card className="rounded-2xl border-[#e5e5e5] p-8">
          <p style={{ color: "#555555" }}>Access Denied: Superadmin privileges required.</p>
          <Button onClick={() => navigate("/dashboard")} className="mt-4 rounded-xl" style={{ backgroundColor: "#652D90" }}>
            Back to Dashboard
          </Button>
        </Card>
      </div>
    );
  }

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-4 py-8 space-y-8">
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-4xl font-bold" style={{ color: "#652D90" }}>System Analytics</h1>
        </div>
        {loading ? (
          <div className="text-center py-12">
            <p style={{ color: "#555555" }}>Loading system analytics...</p>
          </div>
        ) : stats ? (
          <>
            {/* Active Users Section */}
            <div className="mb-8">
              <h3 className="text-xl font-semibold mb-4" style={{ color: "#333333" }}>
                👥 User Activity
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <Card className="rounded-2xl border-[#e5e5e5]">
                  <CardContent className="pt-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm" style={{ color: "#555555" }}>Total Users</p>
                        <p className="text-3xl font-bold mt-1" style={{ color: "#652D90" }}>
                          {stats.activeUsers.total_users}
                        </p>
                      </div>
                      <Users size={32} style={{ color: "#652D90", opacity: 0.3 }} />
                    </div>
                    <div className="mt-4 pt-4 border-t" style={{ borderColor: "#e5e5e5" }}>
                      <div className="grid grid-cols-3 gap-2 text-xs">
                        <div>
                          <p style={{ color: "#666" }}>Admins</p>
                          <p className="font-bold" style={{ color: "#652D90" }}>
                            {stats.activeUsers.users_by_role.clinic_admin}
                          </p>
                        </div>
                        <div>
                          <p style={{ color: "#666" }}>Nurses</p>
                          <p className="font-bold" style={{ color: "#652D90" }}>
                            {stats.activeUsers.users_by_role.nurse}
                          </p>
                        </div>
                        <div>
                          <p style={{ color: "#666" }}>Super</p>
                          <p className="font-bold" style={{ color: "#652D90" }}>
                            {stats.activeUsers.users_by_role.superadmin}
                          </p>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="rounded-2xl border-[#e5e5e5]">
                  <CardContent className="pt-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm" style={{ color: "#555555" }}>Currently Online</p>
                        <p className="text-3xl font-bold mt-1" style={{ color: "#1fb25a" }}>
                          {stats.activeUsers.currently_logged_in}
                        </p>
                        <p className="text-xs mt-1" style={{ color: "#999" }}>Active in last 30 min</p>
                      </div>
                      <Activity size={32} style={{ color: "#1fb25a", opacity: 0.3 }} />
                    </div>
                  </CardContent>
                </Card>

                <Card className="rounded-2xl border-[#e5e5e5]">
                  <CardContent className="pt-6">
                    <div>
                      <p className="text-sm mb-3" style={{ color: "#555555" }}>Active by Role</p>
                      <div className="space-y-2">
                        <div className="flex justify-between items-center">
                          <span className="text-sm" style={{ color: "#666" }}>Admins:</span>
                          <span className="text-lg font-bold" style={{ color: "#652D90" }}>
                            {stats.activeUsers.active_users_by_role.clinic_admin || 0}
                          </span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-sm" style={{ color: "#666" }}>Nurses:</span>
                          <span className="text-lg font-bold" style={{ color: "#652D90" }}>
                            {stats.activeUsers.active_users_by_role.nurse || 0}
                          </span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-sm" style={{ color: "#666" }}>Superadmins:</span>
                          <span className="text-lg font-bold" style={{ color: "#652D90" }}>
                            {stats.activeUsers.active_users_by_role.superadmin || 0}
                          </span>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="rounded-2xl border-[#e5e5e5]">
                  <CardContent className="pt-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm" style={{ color: "#555555" }}>Sessions Today</p>
                        <p className="text-3xl font-bold mt-1" style={{ color: "#652D90" }}>
                          {stats.activeUsers.sessions_today}
                        </p>
                        <p className="text-xs mt-1" style={{ color: "#999" }}>Total logins</p>
                      </div>
                      <Clock size={32} style={{ color: "#652D90", opacity: 0.3 }} />
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>

            {/* Email & SMS Statistics */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Email Statistics */}
              <Card className="rounded-2xl border-[#e5e5e5]">
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2" style={{ color: "#652D90" }}>
                    <Mail size={20} />
                    Email Statistics
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="flex justify-between items-center p-4 rounded-lg" style={{ backgroundColor: "#f5f5f5" }}>
                      <span className="font-semibold" style={{ color: "#555555" }}>Total Emails Sent</span>
                      <span className="text-2xl font-bold" style={{ color: "#652D90" }}>
                        {stats.emailStats.total_emails}
                      </span>
                    </div>

                    <div>
                      <p className="text-sm font-semibold mb-3" style={{ color: "#555555" }}>
                        Top Clinics by Email Volume
                      </p>
                      <div className="space-y-2">
                        {stats.emailStats.emails_by_clinic.length > 0 ? (
                          stats.emailStats.emails_by_clinic.slice(0, 5).map((clinic, idx) => (
                            <div
                              key={idx}
                              className="flex justify-between items-center py-2 px-3 rounded"
                              style={{ backgroundColor: idx % 2 === 0 ? "#f5f5f5" : "#ffffff" }}
                            >
                              <div className="flex items-center gap-2">
                                <span className="text-xs font-bold" style={{ color: "#652D90" }}>
                                  #{idx + 1}
                                </span>
                                <span className="text-sm" style={{ color: "#333333" }}>
                                  {clinic.clinic_name}
                                </span>
                              </div>
                              <span className="text-sm font-bold" style={{ color: "#652D90" }}>
                                {clinic.email_count}
                              </span>
                            </div>
                          ))
                        ) : (
                          <p className="text-sm text-center py-4" style={{ color: "#999999" }}>
                            No email data yet
                          </p>
                        )}
                      </div>
                    </div>

                    <div>
                      <p className="text-sm font-semibold mb-3" style={{ color: "#555555" }}>
                        Last 7 Days Activity
                      </p>
                      {stats.emailStats.emails_per_day.length > 0 ? (
                        <div className="space-y-2">
                          {stats.emailStats.emails_per_day.map((day, idx) => (
                            <div key={idx} className="flex justify-between items-center py-1">
                              <span className="text-xs" style={{ color: "#666666" }}>{day.date}</span>
                              <div className="flex items-center gap-2">
                                <div
                                  className="h-2 rounded-full"
                                  style={{
                                    backgroundColor: "#652D90",
                                    width: `${Math.min(day.count * 20, 100)}px`
                                  }}
                                />
                                <span className="text-xs font-bold" style={{ color: "#652D90" }}>
                                  {day.count}
                                </span>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-sm text-center py-2" style={{ color: "#999999" }}>
                          No recent activity
                        </p>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* SMS Statistics */}
              <Card className="rounded-2xl border-[#e5e5e5]">
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2" style={{ color: "#652D90" }}>
                    <MessageSquare size={20} />
                    SMS Statistics
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-center py-12">
                    <MessageSquare size={64} className="mx-auto mb-4" style={{ color: "#cccccc" }} />
                    <p className="text-lg font-semibold mb-2" style={{ color: "#652D90" }}>
                      SMS Feature Coming Soon
                    </p>
                    <p className="text-sm mb-4" style={{ color: "#666666" }}>
                      SMS tracking and analytics will be available in the next update
                    </p>
                    <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full" style={{ backgroundColor: "#f5f5f5" }}>
                      <TrendingUp size={16} style={{ color: "#652D90" }} />
                      <span className="text-sm" style={{ color: "#652D90" }}>Configure in Settings</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </>
        ) : (
          <div className="text-center py-12">
            <p style={{ color: "#e90d01" }}>Failed to load analytics data</p>
            <Button onClick={loadStats} className="mt-4 rounded-xl" style={{ backgroundColor: "#652D90" }}>
              Retry
            </Button>
          </div>
        )}
      </div>
    </Layout>
  );
};
